import { computed, ComputedRef, ref, Ref } from "vue"
import {
  CompanyBenefitEnrollment,
  BenefitPlan,
  CompanyBenefitPlanTierCost,
  BenefitsApiListOpenCompanyBenefitEnrollmentsForMemberRequest,
  BenefitsApiEnrollIntoCompanyBenefitRequest,
  EnrollIntoCompanyBenefitResponse,
  BenefitsApiWaiveCompanyBenefitRequest,
  WaiveCompanyBenefitResponse,
  ListBenefitPlansOfferedByCompanyBenefitResponse,
  BenefitsApiListBenefitPlansOfferedByCompanyBenefitRequest,
  BenefitsApiListCompanyBenefitPlanTierCostsRequest,
  ListCompanyBenefitPlanTierCostsResponse,
  BenefitsApiProvideEnrollReasonRequest,
  PendingCompanyBenefitEnrollmentDTO,
  ListPendingCompanyBenefitEnrollmentsForMemberResponse,
  BenefitsApiListActiveEnrolleesByDemographicsRequest,
  ListActiveEnrolleesByDemographicsResponse,
  RedactedMember
} from "@vitable/asclepius-api-client-ts"
import useAsclepius from "@common/composables/api/asclepius"

export function useListOpenCompanyBenefitEnrollmentsForMember() {
  const { getBenefitsApi } = useAsclepius()
  const enrollments: Ref<Array<CompanyBenefitEnrollment>> = ref([])

  async function listOpenCompanyBenefitEnrollmentsForMember(
    request: BenefitsApiListOpenCompanyBenefitEnrollmentsForMemberRequest
  ): Promise<Array<CompanyBenefitEnrollment>> {
    enrollments.value = (await getBenefitsApi().listOpenCompanyBenefitEnrollmentsForMember(request)).data.enrollments

    return enrollments.value
  }

  return {
    listOpenCompanyBenefitEnrollmentsForMember,
    enrollments
  }
}

export function useListCompanyBenefitPlans() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function listCompanyBenefitPlans(
    request: BenefitsApiListBenefitPlansOfferedByCompanyBenefitRequest
  ): Promise<void> {
    await call(() => getBenefitsApi().listBenefitPlansOfferedByCompanyBenefit(request))
  }

  const benefitPlans: ComputedRef<Array<BenefitPlan>> = computed(() => {
    const response: Nullable<ListBenefitPlansOfferedByCompanyBenefitResponse> = callResult.value.data
    return response?.benefit_plans ?? []
  })

  return {
    benefitPlans,
    listCompanyBenefitPlans,
    listCompanyBenefitPlansResult: callResult
  }
}

export function useListCompanyBenefitPlanTierCosts() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function listCompanyBenefitPlanTierCosts(
    request: BenefitsApiListCompanyBenefitPlanTierCostsRequest
  ): Promise<void> {
    await call(() => getBenefitsApi().listCompanyBenefitPlanTierCosts(request))
  }

  const companyBenefitPlanTierCosts: ComputedRef<Array<CompanyBenefitPlanTierCost>> = computed(() => {
    const response: Nullable<ListCompanyBenefitPlanTierCostsResponse> = callResult.value.data
    return response?.tier_costs ?? []
  })

  return {
    companyBenefitPlanTierCosts,
    listCompanyBenefitPlanTierCosts,
    listCompanyBenefitPlanTierCostsResult: callResult
  }
}

export function useListPendingCompanyBenefitEnrollments() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function listPendingCompanyBenefitEnrollments(memberId: string): Promise<void> {
    await call(() => getBenefitsApi().listPendingCompanyBenefitEnrollmentsForMember({ memberId }))
  }

  const pendingCompanyBenefitEnrollments: ComputedRef<Array<PendingCompanyBenefitEnrollmentDTO>> = computed(() => {
    const response: Nullable<ListPendingCompanyBenefitEnrollmentsForMemberResponse> = callResult.value.data
    return response?.pending_enrollments ?? []
  })

  return {
    pendingCompanyBenefitEnrollments,
    listPendingCompanyBenefitEnrollments,
    listPendingCompanyBenefitEnrollmentsResult: callResult
  }
}

export function useStartCompanyBenefitEnrollments() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function startCompanyBenefitEnrollments(): Promise<void> {
    await call(() => getBenefitsApi().startCompanyBenefitEnrollments())
  }

  return {
    startCompanyBenefitEnrollments,
    startCompanyBenefitEnrollmentsResult: callResult
  }
}

export function useWaiveCompanyBenefit() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function waiveCompanyBenefit(request: BenefitsApiWaiveCompanyBenefitRequest): Promise<void> {
    await call(() => getBenefitsApi().waiveCompanyBenefit(request))
  }

  const companyBenefitEnrollment: ComputedRef<CompanyBenefitEnrollment | null | undefined> = computed(() => {
    const response: Nullable<WaiveCompanyBenefitResponse> = callResult.value.data
    return response?.enrollment
  })

  return {
    companyBenefitEnrollment,
    waiveCompanyBenefit,
    waiveCompanyBenefitResult: callResult
  }
}

export function useEnrollIntoCompanyBenefit() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function enrollIntoCompanyBenefit(request: BenefitsApiEnrollIntoCompanyBenefitRequest): Promise<void> {
    await call(() => getBenefitsApi().enrollIntoCompanyBenefit(request))
  }

  const enrollments: ComputedRef<Array<CompanyBenefitEnrollment> | null | undefined> = computed(() => {
    const response: Nullable<EnrollIntoCompanyBenefitResponse> = callResult.value.data
    return response?.enrollments
  })

  return {
    enrollments,
    enrollIntoCompanyBenefit,
    enrollIntoCompanyBenefitResult: callResult
  }
}

export function useProvideEnrollReason() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function provideEnrolledReason(request: BenefitsApiProvideEnrollReasonRequest): Promise<void> {
    await call(() => getBenefitsApi().provideEnrollReason(request))
  }

  return {
    provideEnrolledReason,
    provideEnrollReasonResult: callResult
  }
}

export function useListActiveEnrolleesByDemographics() {
  const { getBenefitsApi, call, callResult } = useAsclepius()

  async function listActiveEnrolleesByDemographics(
    request: BenefitsApiListActiveEnrolleesByDemographicsRequest
  ): Promise<void> {
    await call(() => getBenefitsApi().listActiveEnrolleesByDemographics(request))
  }

  const enrollees: ComputedRef<Array<RedactedMember>> = computed(() => {
    const response: Nullable<ListActiveEnrolleesByDemographicsResponse> = callResult.value.data
    return response?.enrollees ?? []
  })

  return {
    listActiveEnrolleesByDemographics,
    listActiveEnrolleesByDemographicsResult: callResult,
    enrollees
  }
}
